import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { autocompletePaperSx, autocompleteTextFieldSx } from "./location-mapping.styles";
import type { DropdownItem } from "./location-mapping.types";
import { useLocationMapping } from "./location-mapping.hook";

export const LocationMappingScreen: React.FC = () => {
  const {
    searchInput,
    setSearchInput,
    filteredRows,
    paginatedRows,
    currentPage,
    totalPages,
    goToPage,
    dropdownItems,
  } = useLocationMapping();

  const [pageInputValue, setPageInputValue] = useState(String(currentPage));

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-6 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-6">
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Danh sách phường, xã trước và sau sáp nhập
        </h1>

        {/* Search */}
        <div className="mb-6">
          <Autocomplete<DropdownItem, false, false, true>
            freeSolo
            options={dropdownItems}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.display)}
            filterOptions={(options) => options}
            inputValue={searchInput}
            onInputChange={(_, value) => setSearchInput(value)}
            noOptionsText="Không tìm thấy gợi ý"
            slotProps={{ paper: { sx: autocompletePaperSx } }}
            renderOption={(props, option) => (
              <li {...props} key={option.normalized} title={option.display}>
                {option.display}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Tìm kiếm theo tỉnh, phường/xã mới hoặc phường/xã cũ…"
                variant="outlined"
                size="small"
                sx={autocompleteTextFieldSx}
              />
            )}
          />

          {/* Result count hint */}
          {searchInput.trim() && (
            <p className="mt-2 text-xs text-gray-500">Tìm thấy {filteredRows.length} kết quả</p>
          )}
        </div>

        {/* Results Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 w-1/5">
                  Tỉnh, thành
                </th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 w-1/5">
                  Phường, xã mới
                </th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 w-3/5">
                  Phường, xã trước sáp nhập
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-800">{row.original.columns[0]}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                      {row.original.columns[1]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.original.columns[2]}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-gray-500 text-sm">
                    Không tìm thấy kết quả phù hợp.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded border text-sm ${
                currentPage === 1
                  ? "text-gray-400 border-gray-200 cursor-not-allowed"
                  : "text-blue-600 border-blue-300 hover:bg-blue-50 cursor-pointer"
              }`}
            >
              ‹
            </button>

            <input
              type="number"
              min={1}
              max={totalPages}
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const page = parseInt(pageInputValue, 10);
                  if (!isNaN(page)) goToPage(page);
                }
              }}
              onBlur={() => setPageInputValue(String(currentPage))}
              className="w-14 text-center border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <span className="text-sm text-gray-600">/ {totalPages}</span>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded border text-sm ${
                currentPage === totalPages
                  ? "text-gray-400 border-gray-200 cursor-not-allowed"
                  : "text-blue-600 border-blue-300 hover:bg-blue-50 cursor-pointer"
              }`}
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
